import { useEffect, useState } from 'react';
import ErrorUtils from 'src/utils/ErrorUtils';
import { ExcelRowConverter, parseExcelFromArrayBuffer } from 'src/utils/ExcelUtils';

// Represents a selectable cell in the Excel sheet
export type SelectableCell = {
    xcellId: string;           // Excel cell address (e.g., "B2", "C3")
    value: string | number;    // The actual cell value
    isSelected: boolean;       // Whether this cell is selected
};

// Represents a row in the Excel table (flattened structure)
export type RowData = {
    rowIndex: number;          // Row index in the Excel sheet (0-based from data start)
    companyCode: string;       // First column value - company code
    [functionName: string]: SelectableCell | string | number; // Dynamic function columns as SelectableCell
};

// Cell identifier for the final output
export type CellIdentifier = {
    xcellId: string;           // Excel cell address (e.g., "B2", "C3")
    companyCode: string;       // Company code from first column
    function: string;          // Function name from column header
    value: string | number;
};


/**
 * Custom hook to fetch and parse source line selection Excel data
 * Parses sheet index 1, with first column as companyCode and other columns as functions
 */
export const useSourceLineExcelData = (preSignedUrl?: string, initialSelections?: CellIdentifier[]) => {
    const [excelData, setExcelData] = useState<RowData[]>([]);
    const [functionHeaders, setFunctionHeaders] = useState<string[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');

    useEffect(() => {
        if (!preSignedUrl) {
            return;
        }

        const fetchAndParseExcel = async () => {
            setLoading(true);
            setError('');

            try {
                // Fetch the Excel file from the preSignedUrl
                const response = await fetch(preSignedUrl);
                if (!response.ok) {
                    throw new Error(`Failed to load excel sheet, received http: ${response.status}`);
                }

                // Get the file as array buffer
                const arrayBuffer = await response.arrayBuffer();

                // Create converter function that transforms raw Excel rows to RowData
                const converter: ExcelRowConverter<RowData> = (rowData: any[], headers: string[], rowIndex: number) => {
                    const row: RowData = {
                        rowIndex,
                        companyCode: rowData[0] != null ? String(rowData[0]) : '',
                    };

                    // Excel row number (1-based, accounting for header row)
                    const excelRowNumber = rowIndex + 2; // +1 for header, +1 for 1-based indexing

                    // Process each function column (skip first column which is companyCode)
                    for (let colIndex = 1; colIndex < headers.length; colIndex++) {
                        const functionName = headers[colIndex];
                        const cellValue = rowData[colIndex];

                        // Calculate Excel cell address (e.g., "B2", "C3")
                        const columnLetter = getExcelColumnLetter(colIndex);
                        const xcellId = `${columnLetter}${excelRowNumber}`;

                        // Check if this cell was previously selected
                        const wasSelected = initialSelections?.some((sel) => sel.xcellId === xcellId) ?? false;

                        // Create SelectableCell for this function column
                        const selectableCell: SelectableCell = {
                            xcellId,
                            value: cellValue != null ? cellValue : '',
                            isSelected: wasSelected,
                        };

                        row[functionName] = selectableCell;
                    }

                    return row;
                };

                // Parse Excel using sheet index 1 (second sheet)
                const parseResult = parseExcelFromArrayBuffer<RowData>(arrayBuffer, converter, {
                    sheetIndex: 1, // Use sheet index 1 as per requirements
                    headerRowIndex: 0,
                    dataStartRowIndex: 1,
                    defaultValue: '',
                });

                if (parseResult.headers.length === 0) {
                    setExcelData([]);
                    setFunctionHeaders([]);
                    return;
                }

                // Extract function headers (all columns except first which is companyCode)
                const functions = parseResult.headers.slice(1);

                setFunctionHeaders(functions);
                setExcelData(parseResult.data);
            } catch (err) {
                setError(ErrorUtils.getMessage(err));
            } finally {
                setLoading(false);
            }
        };

        fetchAndParseExcel();
    }, [preSignedUrl, initialSelections]);

    return {
        excelData,
        functionHeaders,
        loading,
        error,
    };
};


/**
 * Helper function to convert column index to Excel column letter (A, B, C, ..., Z, AA, AB, ...)
 */
function getExcelColumnLetter(columnIndex: number): string {
    let letter = '';
    let index = columnIndex;
    
    while (index >= 0) {
        letter = String.fromCharCode((index % 26) + 65) + letter;
        index = Math.floor(index / 26) - 1;
    }
    
    return letter;
};

